import React, { useState, useEffect, useContext } from 'react';
import {
    Box, Button, Card, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Typography,
    Paper, InputAdornment, Tooltip, Avatar,
    FormControlLabel, Switch, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router';
import { TokenContext } from '../Context/TokenContext';
import { UserContext } from '../Context/UserContext';
import { PERMISSION_KEYS, FINE_PERMISSION_KEYS } from '../constants/permissions';

const API_BASE = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

const emptyForm = { nom: '', prenom: '', email: '', password: '' };

function permissionLabel(key, applications) {
    if (key.startsWith('app:')) {
        const clientId = key.slice(4);
        const app = applications.find(a => a.client_id === clientId);
        return app?.name || clientId;
    }
    const known = PERMISSION_KEYS.find(p => p.key === key) || FINE_PERMISSION_KEYS.find(p => p.key === key);
    return known?.label || key;
}

function initials(nom, prenom) {
    return ((prenom?.[0] || '') + (nom?.[0] || '')).toUpperCase();
}

export default function AdministrateursPage() {
    const navigate = useNavigate();
    const { accessToken } = useContext(TokenContext);
    const { userInfo } = useContext(UserContext);
    const isSuperAdmin = userInfo?.is_super_admin === true;

    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adminsError, setAdminsError] = useState('');
    const [search, setSearch] = useState('');

    const [openDialog, setOpenDialog] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [permTarget, setPermTarget] = useState(null);
    const [permData, setPermData] = useState({ is_super_admin: false, groupes: [], effective_permissions: [] });
    const [permLoading, setPermLoading] = useState(false);
    const [permSaving, setPermSaving] = useState(false);
    const [permError, setPermError] = useState('');

    const [applications, setApplications] = useState([]);

    const headers = { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' };

    const loadAdmins = async () => {
        setLoading(true);
        setAdminsError('');
        try {
            const r = await fetch(`${API_BASE}/api/admins`, { headers });
            const data = await r.json().catch(() => null);
            if (r.ok) {
                setAdmins(data || []);
            } else {
                setAdminsError(data?.error || `Erreur lors du chargement des administrateurs (${r.status}).`);
            }
        } catch (e) {
            setAdminsError('Erreur réseau lors du chargement des administrateurs.');
            console.error('Erreur chargement admins:', e);
        } finally {
            setLoading(false);
        }
    };

    const loadApplications = async () => {
        try {
            const r = await fetch(`${API_BASE}/api/applications`, { headers });
            if (r.ok) setApplications(await r.json());
        } catch (e) {
            console.error('Erreur chargement applications:', e);
        }
    };

    useEffect(() => {
        loadAdmins();
        if (isSuperAdmin) loadApplications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openCreate = () => {
        setEditingAdmin(null);
        setForm(emptyForm);
        setFormError('');
        setShowPassword(false);
        setOpenDialog(true);
    };

    const openEdit = (admin) => {
        setEditingAdmin(admin);
        setForm({ nom: admin.nom, prenom: admin.prenom, email: admin.email, password: '' });
        setFormError('');
        setShowPassword(false);
        setOpenDialog(true);
    };

    const handleSave = async () => {
        if (!form.nom.trim() || !form.prenom.trim() || !form.email.trim()) {
            setFormError('Nom, prénom et email sont obligatoires.');
            return;
        }
        if (!editingAdmin && !form.password.trim()) {
            setFormError('Le mot de passe est obligatoire lors de la création.');
            return;
        }

        setSaving(true);
        setFormError('');
        try {
            const body = { nom: form.nom, prenom: form.prenom, email: form.email };
            if (form.password.trim()) body.password = form.password;

            const url = editingAdmin
                ? `${API_BASE}/api/admins/${editingAdmin.id}`
                : `${API_BASE}/api/admins`;
            const method = editingAdmin ? 'PUT' : 'POST';

            const r = await fetch(url, { method, headers, body: JSON.stringify(body) });
            const data = await r.json();
            if (!r.ok) { setFormError(data.error || 'Erreur lors de la sauvegarde.'); return; }

            // Cette page ne gère que des comptes de direction : un compte fraîchement créé est
            // promu super-admin dans la foulée (POST /api/admins ne pose jamais ce flag lui-même
            // — un compte non super-admin se crée depuis Administration > Employés).
            if (!editingAdmin) {
                const promote = await fetch(`${API_BASE}/api/admins/${data.id}/permissions`, {
                    method: 'PUT', headers, body: JSON.stringify({ is_super_admin: true })
                });
                if (!promote.ok) {
                    setFormError('Compte créé mais impossible de le promouvoir super-admin — réessayez depuis la liste.');
                    loadAdmins();
                    return;
                }
            }

            setOpenDialog(false);
            loadAdmins();
        } catch (e) {
            setFormError('Erreur réseau.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await fetch(`${API_BASE}/api/admins/${deleteTarget.id}`, { method: 'DELETE', headers });
            setDeleteTarget(null);
            loadAdmins();
        } catch (e) {
            console.error('Erreur suppression:', e);
        } finally {
            setDeleting(false);
        }
    };

    const openPermissions = async (admin) => {
        setPermTarget(admin);
        setPermError('');
        setPermLoading(true);
        try {
            const r = await fetch(`${API_BASE}/api/admins/${admin.id}/permissions`, { headers });
            const data = await r.json();
            if (!r.ok) { setPermError(data.error || 'Erreur lors du chargement des permissions.'); return; }
            setPermData({
                is_super_admin: !!data.is_super_admin,
                groupes: data.groupes || [],
                effective_permissions: data.effective_permissions || data.permissions || [],
            });
        } catch (e) {
            setPermError('Erreur réseau.');
        } finally {
            setPermLoading(false);
        }
    };

    // Ne pose que is_super_admin — les groupes/permissions individuelles se gèrent depuis
    // Administration > Gestion des groupes (voir GroupesPage.js), jamais depuis ce dialogue.
    const handleSavePermissions = async () => {
        if (!permTarget) return;
        setPermSaving(true);
        setPermError('');
        try {
            const r = await fetch(`${API_BASE}/api/admins/${permTarget.id}/permissions`, {
                method: 'PUT', headers, body: JSON.stringify({ is_super_admin: permData.is_super_admin })
            });
            const data = await r.json();
            if (!r.ok) { setPermError(data.error || 'Erreur lors de la sauvegarde.'); return; }
            setPermTarget(null);
            loadAdmins();
        } catch (e) {
            setPermError('Erreur réseau.');
        } finally {
            setPermSaving(false);
        }
    };

    // Cette page est réservée à la direction : seuls les comptes super-admin y apparaissent.
    // Les autres comptes (employés) se gèrent depuis Administration > Employés.
    const filtered = admins.filter(a => a.is_super_admin).filter(a => {
        const q = search.toLowerCase();
        return (
            a.nom?.toLowerCase().includes(q) ||
            a.prenom?.toLowerCase().includes(q) ||
            a.email?.toLowerCase().includes(q)
        );
    });

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100, mx: 'auto' }}>
            {/* Header */}
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={2} mb={3}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <AdminPanelSettingsIcon color="primary" sx={{ fontSize: 32 }} />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>Administrateurs</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Comptes de direction, accès total au backoffice
                        </Typography>
                    </Box>
                </Stack>
                {isSuperAdmin && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={openCreate}
                    >
                        Nouvel administrateur
                    </Button>
                )}
            </Stack>

            {adminsError && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>{adminsError}</Typography>
            )}

            {/* Search + count */}
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" mb={2} spacing={2}>
                <TextField
                    size="small"
                    placeholder="Rechercher un administrateur..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                    sx={{ minWidth: { xs: '100%', sm: 280 } }}
                />
                <Chip
                    label={`${filtered.length} administrateur${filtered.length !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{ bgcolor: 'primary.main', color: '#fff', alignSelf: { xs: 'flex-start', sm: 'center' } }}
                />
            </Stack>

            {/* Table */}
            <Card elevation={2}>
                <TableContainer component={Paper} elevation={0}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                                <TableCell width={56}></TableCell>
                                <TableCell><strong>Nom</strong></TableCell>
                                <TableCell><strong>Prénom</strong></TableCell>
                                <TableCell><strong>Email</strong></TableCell>
                                <TableCell><strong>Créé le</strong></TableCell>
                                <TableCell align="right"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={28} sx={{ color: 'primary.main' }} />
                                    </TableCell>
                                </TableRow>
                            ) : filtered.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        {search ? 'Aucun résultat pour cette recherche.' : 'Aucun administrateur enregistré.'}
                                    </TableCell>
                                </TableRow>
                            ) : filtered.map(admin => (
                                <TableRow key={admin.id} hover>
                                    <TableCell>
                                        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: 14 }}>
                                            {initials(admin.nom, admin.prenom)}
                                        </Avatar>
                                    </TableCell>
                                    <TableCell>
                                        <strong>{admin.nom}</strong>
                                        {admin.is_super_admin && (
                                            <Chip label="Super-admin" size="small" sx={{ ml: 1, bgcolor: 'primary.main', color: '#fff' }} />
                                        )}
                                    </TableCell>
                                    <TableCell>{admin.prenom}</TableCell>
                                    <TableCell>{admin.email}</TableCell>
                                    <TableCell>
                                        {admin.createdAt
                                            ? new Date(admin.createdAt).toLocaleDateString('fr-FR')
                                            : '—'}
                                    </TableCell>
                                    <TableCell align="right">
                                        {isSuperAdmin && (
                                            <Tooltip title="Permissions">
                                                <IconButton size="small" onClick={() => openPermissions(admin)}>
                                                    <VpnKeyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Modifier">
                                            <IconButton size="small" onClick={() => openEdit(admin)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => setDeleteTarget(admin)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {/* Dialog création / édition */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingAdmin ? 'Modifier l\'administrateur' : 'Créer un administrateur'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        {formError && (
                            <Typography color="error" variant="body2">{formError}</Typography>
                        )}
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Prénom"
                                value={form.prenom}
                                onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))}
                                fullWidth
                                required
                                autoFocus
                            />
                            <TextField
                                label="Nom"
                                value={form.nom}
                                onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                                fullWidth
                                required
                            />
                        </Stack>
                        <TextField
                            label="Email"
                            type="email"
                            value={form.email}
                            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                            fullWidth
                            required
                        />
                        <TextField
                            label={editingAdmin ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe'}
                            type={showPassword ? 'text' : 'password'}
                            value={form.password}
                            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                            fullWidth
                            required={!editingAdmin}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(v => !v)} edge="end">
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)} disabled={saving}>Annuler</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        color="primary"
                        disabled={saving}
                        >
                        {saving ? <CircularProgress size={20} color="inherit" /> : (editingAdmin ? 'Enregistrer' : 'Créer')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog confirmation suppression */}
            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Supprimer l'administrateur</DialogTitle>
                <DialogContent>
                    <Typography>
                        Voulez-vous supprimer le compte de{' '}
                        <strong>{deleteTarget?.prenom} {deleteTarget?.nom}</strong> ?
                        Cette action est irréversible.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Annuler</Button>
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        color="error"
                        disabled={deleting}
                    >
                        {deleting ? <CircularProgress size={20} color="inherit" /> : 'Supprimer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog permissions (super-admin uniquement) */}
            <Dialog open={!!permTarget} onClose={() => setPermTarget(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Permissions de {permTarget?.prenom} {permTarget?.nom}
                </DialogTitle>
                <DialogContent>
                    {permLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={28} sx={{ color: 'primary.main' }} />
                        </Box>
                    ) : (
                        <Stack spacing={2} mt={1}>
                            {permError && (
                                <Typography color="error" variant="body2">{permError}</Typography>
                            )}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={permData.is_super_admin}
                                        onChange={e => setPermData(d => ({ ...d, is_super_admin: e.target.checked }))}
                                    />
                                }
                                label="Super-administrateur (accès total, peut gérer les autres administrateurs)"
                            />
                            <Divider />
                            <Typography variant="subtitle2" color="text.secondary">
                                Groupes d'appartenance
                            </Typography>
                            {permData.groupes.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">Aucun groupe.</Typography>
                            ) : (
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {permData.groupes.map(g => (
                                        <Chip key={g.id_groupe} label={g.nom} size="small" variant="outlined" />
                                    ))}
                                </Stack>
                            )}
                            <Divider />
                            <Typography variant="subtitle2" color="text.secondary">
                                Permissions effectives {permData.is_super_admin && '(ignoré : accès total via super-administrateur)'}
                            </Typography>
                            {permData.effective_permissions.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">Aucune permission.</Typography>
                            ) : (
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {permData.effective_permissions.map(key => (
                                        <Chip key={key} label={permissionLabel(key, applications)} size="small" />
                                    ))}
                                </Stack>
                            )}
                            <Typography variant="caption" color="text.secondary">
                                Les groupes et permissions individuelles se modifient depuis{' '}
                                <Box component="span" sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                                     onClick={() => navigate('/groupes')}>
                                    Administration &gt; Gestion des groupes
                                </Box>.
                            </Typography>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPermTarget(null)} disabled={permSaving}>Annuler</Button>
                    <Button
                        onClick={handleSavePermissions}
                        variant="contained"
                        color="primary"
                        disabled={permSaving || permLoading}
                        >
                        {permSaving ? <CircularProgress size={20} color="inherit" /> : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
